import cn from 'classnames'
import React, { useRef, useMemo } from 'react'

import { SiteDataContext } from '../../components'
import Image from 'next/image'
import Icon from '../IconNew'
import { useLockBodyScroll, useOnClickOutside } from '../hooks'
import { useGlobalData } from '@/pages/_app'
import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import { getMicroCopy } from '@/src/utils/microcopy'
import Link from 'next/link'

export default function SideDrawer({ isOpen, languageLocationMenu, handleIsOpen }) {
  const rootEl = useRef(null)
  const { isTextRtl, lang } = React.useContext(SiteDataContext)
  const { markets } = useGlobalData()
  const microCopyData = useMicroCopyData()

  const marketList = useMemo(
    () =>
      markets.map((market) => {
        const m = { ...market }
        m.languages = market.locales.map((locale) => {
          const language = locale.split('-')[0]
          return {
            label: getMicroCopy({
              key: `languages.${language}`,
              data: microCopyData[lang]
            }),
            slug: `/${locale}`
          }
        })

        m.title = getMicroCopy({
          key: `market.${m.countryIsoCode.toLowerCase()}`,
          fallback: m.title,
          data: microCopyData[lang]
        })

        return m
      }),
    [markets, lang]
  )

  const { title } = languageLocationMenu

  useOnClickOutside(rootEl, (e) => {
    if (e && e.target.className === 'c-header__market-selector') return
    if (isOpen) {
      e.stopImmediatePropagation()
    }
    handleIsOpen(false)
  })

  useLockBodyScroll(isOpen)

  const renderLanguageLocation = useMemo(() => (
    <div className="c-sidedrawer__dropdown__container">
      <ul className="c-sidedrawer__markets-list">
        {marketList.map(({ id, title, languages, svgFlag, svgFlagRound }) => {
          return (
            <li className="c-market-selector__option" key={`sidedrawer-${id}`}>
              <Image
                className="c-market-selector__option__image"
                priority
                src={svgFlagRound?.url || svgFlag?.url}
                width={24}
                height={24}
                alt={`${title} flag`}
              />
              <div className="c-market-selector__option__content">
                <div className="c-market-selector__option__content__language-label">{title}</div>
                <div className="c-market-selector__option__content__languages-container">
                  {languages.map((l) => {
                    return (
                      <Link
                        href={l.slug}
                        locale={false}
                        className="c-market-selector__option__content__language"
                        key={`sidedrawer-${id}=${l.label}`}
                      >
                        {l.label}
                      </Link>
                    )
                  })}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  ))

  return (
    <div
      ref={rootEl}
      className={cn('c-sidedrawer', {
        'is-open': isOpen,
        'c-sidedrawer--rtl': isTextRtl
      })}
    >
      <div className="c-sidedrawer__location-container" dir={isTextRtl ? 'rtl' : 'auto'}>
        <div className="c-sidedrawer__header ">
          <button
            aria-label="Close Button"
            className={'c-sidedrawer__header__icon'}
            onClick={() => {
              handleIsOpen(false)
            }}
          >
            <Icon name={'tabler-x'} classes={''} />
          </button>
          {title}
        </div>
        {renderLanguageLocation}
      </div>
    </div>
  )
}
